import { Pipe, PipeTransform } from "@angular/core";
import { Conversation } from "../entities/conversation.entity";
import { Message, SenderRole } from "../entities/message.entity";
import { RequestType } from "../entities/message-request.entity";

@Pipe({
  name: "filterConversationsWithPendingRequests",
  standalone: true,
})
export class FilterConversationsWithPendingRequestsPipe implements PipeTransform {
  transform(conversations: Conversation[]): Conversation[] {
    if (!conversations) return [];
    return conversations.filter((conversation) => this.hasPendingPaymentRequest(conversation));
  }

  hasPendingPaymentRequest(conversation: Conversation): boolean {
    if (!conversation?.messages || conversation.messages.length === 0) {
      return false;
    }

    return conversation.messages.some((message: Message) => {
      // Check if message is from project manager with a payment request
      if (message.senderRole !== SenderRole.PROJECT_MANAGER || !message.requestId) {
        return false;
      }

      // Check if request is for milestone completion (payment request)
      if (message.request?.requestType !== RequestType.MILESTONE_COMPLETED) {
        return false;
      }

      // Check if request has no response (pending - client hasn't responded)
      if (message.responseId) {
        return false;
      }

      return true;
    });
  }

  hasUnreadMessages(conversation: Conversation): boolean {
    if (!conversation?.messages || conversation.messages.length === 0) {
      return false;
    }

    return conversation.messages.some((message: Message) => {
      // Only check messages from client (business reads client messages)
      // read_at semantics: if sender is client, read_at means business read it; otherwise it means client read it
      if (message.senderRole !== SenderRole.CLIENT) {
        return false;
      }

      // Skip payment requests (they are handled by hasPendingPaymentRequest)
      if (message.requestId && message.request?.requestType === RequestType.MILESTONE_COMPLETED) {
        return false;
      }

      // Message is unread if readAt is null (never read by business)
      return message.readAt === null || message.readAt === undefined;
    });
  }
}

@Pipe({
  name: "countConversationsWithPendingRequests",
  standalone: true,
})
export class CountConversationsWithPendingRequestsPipe implements PipeTransform {
  transform(conversations: Conversation[]): number {
    if (!conversations) return 0;
    const filterPipe = new FilterConversationsWithPendingRequestsPipe();
    // Count conversations with unread messages only (Action Required is hidden for business)
    return conversations.filter((conversation) => filterPipe.hasUnreadMessages(conversation)).length;
  }
}

@Pipe({
  name: "countUnreadMessages",
  standalone: true,
})
export class CountUnreadMessagesPipe implements PipeTransform {
  transform(conversations: Conversation[]): number {
    if (!conversations) return 0;
    const filterPipe = new FilterConversationsWithPendingRequestsPipe();
    
    let totalUnread = 0;
    conversations.forEach((conversation) => {
      if (!conversation?.messages || conversation.messages.length === 0) {
        return;
      }

      conversation.messages.forEach((message: Message) => {
        // Only check messages from client (business reads client messages)
        // read_at semantics: if sender is client, read_at means business read it; otherwise it means client read it
        if (message.senderRole !== SenderRole.CLIENT) {
          return;
        }

        // Skip payment requests (they are handled by hasPendingPaymentRequest)
        if (message.requestId && message.request?.requestType === RequestType.MILESTONE_COMPLETED) {
          return;
        }

        // Message is unread if readAt is null (never read by business)
        if (message.readAt === null || message.readAt === undefined) {
          totalUnread++;
        }
      });
    });

    return totalUnread;
  }
}
